// The bottom-right upload progress tray, shared by the Investigations bulk
// uploader and the Files library uploader so both look and behave the same:
// "Uploading 2 of 5", a progress bar for the current file, a per-file list,
// collapse, and a dismiss that plays the slide-down before clearing.
//
// Presentational only: the caller owns the items and decides what the copy says.
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { useEffect, useState } from "react";

// "processing": the file is uploaded and the server is working on it
// (transcribing, reading). It can still end in "done" or "error".
export type TrayStatus = "hashing" | "queued" | "uploading" | "processing" | "done" | "error" | "duplicate" | "skipped";

export interface TrayItem {
  id: string;
  name: string;
  status: TrayStatus;
  progress: number;
  // Shown for error / skipped items.
  error?: string;
  // Shown on a processing or finished item, e.g. "transcribing" or "queued".
  doneLabel?: string;
  // When set, the name becomes a link (e.g. open the finished template).
  onOpen?: () => void;
}

const TRAY_LEAVE_MS = 220;
const IN_PROGRESS: TrayStatus[] = ["hashing", "queued", "uploading"];

export const UploadTrayView: React.FC<{
  items: TrayItem[];
  // Called after the dismiss animation; remove finished items here.
  onClearFinished: () => void;
  // Subtitle under the title while uploading, and once everything has finished.
  activeHint: string;
  finishedHint: string;
  // Verb for the finished title: "Uploaded 3 of 5" / "Saved 3 of 5".
  finishedVerb?: string;
  // Adds an X to files that are waiting or uploading, to stop them.
  onCancel?: (id: string) => void;
}> = ({ items, onClearFinished, activeHint, finishedHint, finishedVerb = "Uploaded", onCancel }) => {
  const [collapsed, setCollapsed] = useState(false);
  // Dismiss plays the slide-down first, then clears the finished items (which
  // unmounts the tray). A new batch arriving mid-exit cancels it.
  const [leaving, setLeaving] = useState(false);
  const active = items.some((i) => IN_PROGRESS.includes(i.status));
  const processing = items.filter((i) => i.status === "processing").length;
  useEffect(() => {
    if (active) setLeaving(false);
  }, [active]);
  const dismiss = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => {
      onClearFinished();
      setLeaving(false);
    }, TRAY_LEAVE_MS);
  };

  if (!items.length) return null;
  const done = items.filter((i) => i.status === "done").length;
  const failed = items.filter((i) => i.status === "error" || i.status === "skipped").length;
  const dupes = items.filter((i) => i.status === "duplicate").length;
  const current = items.find((i) => i.status === "uploading");

  return (
    <div
      data-tour="upload-tray"
      className={cn(
        "fixed bottom-4 right-4 z-40 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-base-300 bg-base-100 shadow-lg",
        leaving ? "miniplayer-leave" : "miniplayer-enter",
      )}
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-base-300">
        <div className="text-sm">
          <p className="font-semibold text-base-content">
            {active
              ? `Uploading ${done + processing + 1} of ${items.length}`
              : processing > 0
                ? `Processing ${processing} file${processing === 1 ? "" : "s"}`
                : `${finishedVerb} ${done} of ${items.length}`}
          </p>
          <p className="text-xs text-base-content/60">
            {active ? activeHint : finishedHint}
            {dupes ? ` ${dupes} duplicate${dupes === 1 ? "" : "s"} skipped.` : ""}
            {failed ? ` ${failed} failed.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1 rounded hover:bg-base-200" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? <ChevronUpIcon className="size-4" /> : <ChevronDownIcon className="size-4" />}
          </button>
          {!active && processing === 0 && (
            <button className="p-1 rounded hover:bg-base-200" onClick={dismiss} title="Dismiss">
              <XMarkIcon className="size-4" />
            </button>
          )}
        </div>
      </div>
      {current && (
        <div className="h-1 bg-base-200">
          <div className="h-1 bg-blue-500 transition-all" style={{ width: `${current.progress}%` }} />
        </div>
      )}
      {!collapsed && (
        <ul className="max-h-64 overflow-auto divide-y divide-base-200 text-sm">
          {items.map((i) => {
            const isError = i.status === "error" || i.status === "skipped";
            return (
              <li key={i.id} className="px-4 py-1.5">
                <div className="flex items-center gap-2">
                  {i.status === "done" ? (
                    <CheckCircleIcon className="size-4 text-green-600 dark:text-green-400 shrink-0" />
                  ) : isError ? (
                    <ExclamationCircleIcon className="size-4 text-red-600 dark:text-red-400 shrink-0" />
                  ) : i.status === "duplicate" ? (
                    <ExclamationCircleIcon className="size-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  ) : i.status === "processing" ? (
                    <span className="loading loading-spinner size-3.5 text-blue-500 shrink-0" />
                  ) : (
                    <span className={cn("size-3 rounded-full shrink-0", i.status === "uploading" ? "bg-blue-500 animate-pulse" : "bg-base-300")} />
                  )}
                  {i.onOpen ? (
                    <button
                      type="button"
                      className="truncate flex-1 text-left text-base-content hover:text-blue-600 dark:hover:text-blue-300 hover:underline"
                      onClick={i.onOpen}
                    >
                      {i.name}
                    </button>
                  ) : (
                    <span className="truncate flex-1 text-base-content">{i.name}</span>
                  )}
                  <span className={cn("text-xs shrink-0", isError ? "text-red-600 dark:text-red-400 font-medium" : "text-base-content/60")}>
                    {i.status === "uploading"
                      ? `${i.progress}%`
                      : i.status === "hashing"
                        ? "hashing"
                        : i.status === "duplicate"
                          ? "duplicate"
                          : isError
                            ? "Failed"
                            : i.status === "processing" || i.status === "done"
                              ? i.doneLabel ?? ""
                              : ""}
                  </span>
                  {onCancel && (i.status === "queued" || i.status === "uploading") && (
                    <button
                      type="button"
                      className="p-0.5 -mr-1 rounded text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-base-200 shrink-0"
                      title={i.status === "uploading" ? "Stop uploading" : "Remove from the queue"}
                      aria-label={`Stop uploading ${i.name}`}
                      onClick={() => onCancel(i.id)}
                    >
                      <XMarkIcon className="size-3.5" />
                    </button>
                  )}
                </div>
                {/* Why it failed, in full, under the name */}
                {isError && <p className="mt-0.5 ml-6 text-xs text-red-600/90 dark:text-red-400/90 break-words">{i.error || "Something went wrong."}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UploadTrayView;
